import { type ReactNode } from 'react'
import {
  Dropdown,
  DropdownTitle,
  DropdownDivider,
  DropdownItem,
  type CheckboxState,
  DropdownScroll,
} from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  Droppable,
} from '@hello-pangea/dnd'
import cx from 'classnames'
import styles from './TableControls.module.css'
import { type ColumnDensity } from '@/components/Table/TableCell'
import { match } from '@/utils/pattern-match-util'
import { pluralize } from '@/utils/pluralization-util'

export interface TableControlsProps {
  button: ReactNode
  onExportCsv: () => void
  onManageColumns: () => void
  canNotifyPros: boolean
  onNotifyPros: () => void
  selectedCount: number
  /**
   * `null` when the current selection can be neither archived nor restored.
   */
  archiveRestoreAction: 'Archive' | 'Restore' | null
  onArchiveRestore: () => void
  canClone: boolean
  onClone: () => void
  canTransfer: boolean
  onTransfer: () => void
  canAdvance: boolean
  onAdvance: () => void
  density: ColumnDensity
  onDensityChange: (density: ColumnDensity) => void
  columnControls: ReactNode
}

export function TableControls({
  button,
  onExportCsv,
  onManageColumns,
  canNotifyPros,
  onNotifyPros,
  selectedCount,
  archiveRestoreAction,
  onArchiveRestore,
  canClone,
  onClone,
  canTransfer,
  onTransfer,
  canAdvance,
  onAdvance,
  density,
  onDensityChange,
  columnControls,
}: TableControlsProps) {
  return (
    <>
      <Dropdown autoDismiss position="bottom-end" button={button}>
        <div className={styles.tableControlsContainer}>
          <DropdownTitle>Menu</DropdownTitle>
          <TableControlsItem
            icon={
              <Icon
                className={styles.tableControlsItemIcon}
                name="download"
                size={24}
              />
            }
            onPress={onExportCsv}
            testId="job-management-table-menu-export-csv"
          >
            Export CSV
          </TableControlsItem>
          <TableControlsItem
            icon={
              <Icon
                className={styles.tableControlsItemIcon}
                name="settings-nav"
                size={24}
              />
            }
            onPress={onManageColumns}
            testId="job-management-table-menu-manage-columns"
          >
            Manage columns
          </TableControlsItem>
          {canNotifyPros && (
            <TableControlsItem
              icon={
                <Icon
                  className={styles.tableControlsItemIcon}
                  name="notifications"
                  size={24}
                />
              }
              onPress={onNotifyPros}
              testId="job-management-table-menu-notify-pros-unclaimed"
            >
              Notify Pros of unclaimed jobs
            </TableControlsItem>
          )}
          {selectedCount > 0 && (
            <>
              <DropdownDivider />
              <DropdownTitle>Selection</DropdownTitle>
              {archiveRestoreAction && (
                <TableControlsItem
                  icon={match(archiveRestoreAction, {
                    Restore: () => (
                      <Icon
                        className={styles.tableControlsItemIcon}
                        name="restore"
                        size={24}
                      />
                    ),
                    Archive: () => (
                      <Icon
                        className={styles.tableControlsItemIcon}
                        name="delete"
                        size={24}
                      />
                    ),
                  })}
                  onPress={onArchiveRestore}
                  testId="job-management-table-menu-archive-restore-jobs"
                >
                  {match(archiveRestoreAction, {
                    Restore: () => 'Restore archived jobs',
                    Archive: () => 'Archive jobs',
                  })}
                </TableControlsItem>
              )}
              {canClone && (
                <TableControlsItem
                  icon={
                    <Icon
                      className={styles.tableControlsItemIcon}
                      name="copy"
                      size={24}
                    />
                  }
                  onPress={onClone}
                  testId="job-management-table-menu-clone-jobs"
                >
                  Clone Jobs
                </TableControlsItem>
              )}
              {canTransfer && (
                <TableControlsItem
                  icon={
                    <Icon
                      className={styles.tableControlsItemIcon}
                      name="transfer"
                      size={24}
                    />
                  }
                  onPress={onTransfer}
                  testId="job-management-table-menu-bulk-transfer-jobs"
                >
                  Transfer {selectedCount > 1 ? `${selectedCount} Jobs` : 'Job'}
                </TableControlsItem>
              )}
              {canAdvance && (
                <TableControlsItem
                  icon={
                    <Icon
                      className={styles.tableControlsItemIcon}
                      name="play"
                      size={24}
                    />
                  }
                  onPress={onAdvance}
                  testId="job-management-table-menu-advance-jobs"
                >
                  Advance {selectedCount} {pluralize('job', selectedCount)}
                </TableControlsItem>
              )}
            </>
          )}

          <DropdownDivider />
          <DropdownTitle>Density</DropdownTitle>
          <TableControlsItem
            icon={
              <Icon
                className={cx(
                  styles.tableControlsItemIcon,
                  density === 'Default' && styles.activeIcon,
                )}
                name="density-default"
                size={24}
              />
            }
            active={density === 'Default'}
            onPress={() => onDensityChange('Default')}
            testId="job-management-table-menu-density-default"
          >
            Default
          </TableControlsItem>
          <TableControlsItem
            icon={
              <Icon
                className={cx(
                  styles.tableControlsItemIcon,
                  density === 'Comfortable' && styles.activeIcon,
                )}
                name="density-comfortable"
                size={24}
              />
            }
            active={density === 'Comfortable'}
            onPress={() => onDensityChange('Comfortable')}
            testId="job-management-table-menu-density-comfortable"
          >
            Comfortable
          </TableControlsItem>
          <TableControlsItem
            icon={
              <Icon
                className={cx(
                  styles.tableControlsItemIcon,
                  density === 'Compact' && styles.activeIcon,
                )}
                name="density-compact"
                size={24}
              />
            }
            active={density === 'Compact'}
            onPress={() => onDensityChange('Compact')}
            testId="job-management-table-menu-density-compact"
          >
            Compact
          </TableControlsItem>
        </div>
      </Dropdown>
      {columnControls}
    </>
  )
}

export interface ManagedColumn {
  id: string
  header: string
  visible: boolean
  modifyVisibility: boolean
  actionSelector?: boolean
}

export interface ColumnControlsProps {
  isOpen: boolean
  onRequestClose: () => void
  allColumnsVisible: boolean
  onToggleAllColumns: () => void
  columns: ManagedColumn[]
  onToggleColumn: (id: string) => void
  onReorderColumn: (from: number, to: number | undefined) => void
}

export function ColumnControls({
  isOpen,
  onRequestClose,
  allColumnsVisible,
  onToggleAllColumns,
  columns,
  onToggleColumn,
  onReorderColumn,
}: ColumnControlsProps) {
  return (
    <Dropdown
      position="bottom-end"
      isOpen={isOpen}
      onRequestClose={onRequestClose}
    >
      <DropdownTitle>Manage columns</DropdownTitle>
      <ColumnControlsItem
        checkboxState={allColumnsVisible ? 'checked' : 'indeterminate'}
        onPress={onToggleAllColumns}
        testId="column-manager-all-columns"
      >
        All Columns
      </ColumnControlsItem>
      <DropdownDivider />
      <DragDropContext
        onDragEnd={(r) => onReorderColumn(r.source.index, r.destination?.index)}
      >
        <Droppable
          droppableId="column-manager"
          renderClone={(provided, snapshot, rubric) => {
            const column = columns[rubric.source.index]
            return (
              <RearrangeableColumn
                column={column}
                onToggle={onToggleColumn}
                provided={provided}
                snapshot={snapshot}
              />
            )
          }}
        >
          {(droppableProvided) => (
            <DropdownScroll
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
              {columns.map((column, index) =>
                !column.actionSelector ? (
                  <Draggable
                    disableInteractiveElementBlocking={true}
                    key={column.id}
                    draggableId={column.id}
                    index={index}
                  >
                    {(draggableProvided) => (
                      <RearrangeableColumn
                        column={column}
                        onToggle={onToggleColumn}
                        provided={draggableProvided}
                      />
                    )}
                  </Draggable>
                ) : null,
              )}
              {droppableProvided.placeholder}
            </DropdownScroll>
          )}
        </Droppable>
      </DragDropContext>
    </Dropdown>
  )
}

interface TableControlsItemProps {
  children: ReactNode
  icon: ReactNode
  active?: boolean
  onPress?: () => void
  testId?: string
}

function TableControlsItem({
  children,
  icon,
  active,
  onPress,
  testId,
}: TableControlsItemProps) {
  return (
    <DropdownItem
      icon={icon}
      active={active}
      onPress={onPress}
      buttonTestId={testId}
    >
      {children}
    </DropdownItem>
  )
}

interface RearrangeableColumnProps {
  column: ManagedColumn
  onToggle: (id: string) => void
  provided?: DraggableProvided
  snapshot?: DraggableStateSnapshot
}

function RearrangeableColumn({
  column,
  onToggle,
  provided,
  snapshot,
}: RearrangeableColumnProps) {
  return (
    <ColumnControlsItem
      draggableProvided={provided}
      checkboxState={column.visible ? 'checked' : 'unchecked'}
      visibilityModifiable={column.modifyVisibility}
      elevated={snapshot?.isDragging}
      onPress={() => onToggle(column.id)}
      testId={`column-manager-${column.header.replace(' ', '-').toLowerCase()}`}
    >
      {column.header}
    </ColumnControlsItem>
  )
}

interface ColumnControlsItemProps {
  children: ReactNode
  checkboxState: CheckboxState
  visibilityModifiable?: boolean
  elevated?: boolean
  onPress?: () => void
  draggableProvided?: DraggableProvided
  testId?: string
}

function ColumnControlsItem({
  children,
  checkboxState,
  visibilityModifiable = true,
  draggableProvided,
  elevated,
  onPress,
  testId,
}: ColumnControlsItemProps) {
  return (
    <DropdownItem
      ref={draggableProvided?.innerRef}
      {...draggableProvided?.draggableProps}
      {...draggableProvided?.dragHandleProps}
      checkboxState={checkboxState}
      isDisabled={!visibilityModifiable}
      draggable={Boolean(draggableProvided)}
      isDragging={elevated}
      onPress={onPress}
      buttonTestId={testId}
    >
      {children}
    </DropdownItem>
  )
}
